/**
 * HR Module - Dynamic Form Component
 * Refactored to avoid nested component definitions
 */
import { useState } from 'react';
import { Save } from 'lucide-react';

// Standalone Input component
const FormInput = ({ label, name, type = 'text', required, value, onChange, ...props }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">{label}{required && ' *'}</label>
    <input 
      type={type} 
      value={value || ''} 
      onChange={onChange} 
      required={required} 
      className="w-full px-3 py-2 border rounded-lg text-sm" 
      {...props} 
    />
  </div>
);

// Standalone Select component
const FormSelect = ({ label, name, options, required, value, onChange }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">{label}{required && ' *'}</label>
    <select 
      value={value || ''} 
      onChange={onChange} 
      required={required} 
      className="w-full px-3 py-2 border rounded-lg text-sm"
    >
      <option value="">Pilih...</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

// Standalone Textarea component
const FormTextarea = ({ label, name, rows = 2, value, onChange }) => (
  <div className="col-span-2">
    <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
    <textarea 
      value={value || ''} 
      onChange={onChange} 
      rows={rows} 
      className="w-full px-3 py-2 border rounded-lg text-sm" 
    />
  </div>
);

// Standalone Checkbox component
const FormCheckbox = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-2 cursor-pointer">
    <input 
      type="checkbox" 
      checked={checked || false} 
      onChange={onChange} 
      className="rounded" 
    />
    <span className="text-sm">{label}</span>
  </label>
);

// Standalone Staff Select component
const StaffSelect = ({ staff, value, onChange }) => (
  <div className="col-span-2">
    <label className="block text-xs font-medium text-gray-700 mb-1">Staf *</label>
    <select 
      value={value || ''} 
      onChange={onChange} 
      required 
      className="w-full px-3 py-2 border rounded-lg text-sm"
    >
      <option value="">Pilih staf...</option>
      {staff.map(s => <option key={s.id} value={s.id}>{s.name} - {s.department}</option>)}
    </select>
  </div>
);

const DynamicForm = ({ type, item, staff, dash, onSave, onClose }) => {
  const activeStaff = staff.filter(s => s.status === 'Active');
  const [form, setForm] = useState(item || {});
  
  const up = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const handleSubmit = (e) => { e.preventDefault(); onSave(form); };
  
  // Helper for input change
  const inputChange = (name, inputType) => (e) => {
    up(name, inputType === 'number' ? parseFloat(e.target.value) || 0 : e.target.value);
  };
  
  // Helper for checkbox change
  const checkChange = (name) => (e) => up(name, e.target.checked);

  const renderFormFields = () => {
    switch (type) {
      case 'kpi':
        return (
          <>
            <StaffSelect staff={activeStaff} value={form.staff_id} onChange={e => up('staff_id', e.target.value)} />
            <FormInput label="Tempoh" name="period" placeholder="2024-Q4" required value={form.period} onChange={inputChange('period', 'text')} />
            <FormInput label="Nama KPI" name="kpi_name" required value={form.kpi_name} onChange={inputChange('kpi_name', 'text')} />
            <FormInput label="Sasaran" name="target_value" type="number" required value={form.target_value} onChange={inputChange('target_value', 'number')} />
            <FormInput label="Sebenar" name="actual_value" type="number" value={form.actual_value} onChange={inputChange('actual_value', 'number')} />
            <FormInput label="Unit" name="unit" placeholder="%, RM, unit" value={form.unit} onChange={inputChange('unit', 'text')} />
            <FormSelect label="Status" name="status" options={['Dalam Proses', 'Tercapai', 'Tidak Tercapai']} value={form.status} onChange={e => up('status', e.target.value)} />
          </>
        );

      case 'performance':
        return (
          <>
            <StaffSelect staff={activeStaff} value={form.staff_id} onChange={e => up('staff_id', e.target.value)} />
            <FormInput label="Tempoh" name="period" placeholder="2024-Q4" required value={form.period} onChange={inputChange('period', 'text')} />
            <FormSelect label="Penilaian" name="rating" options={dash?.ratings || []} required value={form.rating} onChange={e => up('rating', e.target.value)} />
            <FormInput label="KPI" name="kpi_score" type="number" value={form.kpi_score} onChange={inputChange('kpi_score', 'number')} />
            <FormInput label="Ketepatan Masa" name="punctuality_score" type="number" value={form.punctuality_score} onChange={inputChange('punctuality_score', 'number')} />
            <FormInput label="Kerja Berpasukan" name="teamwork_score" type="number" value={form.teamwork_score} onChange={inputChange('teamwork_score', 'number')} />
            <FormInput label="Inisiatif" name="initiative_score" type="number" value={form.initiative_score} onChange={inputChange('initiative_score', 'number')} />
            <FormInput label="Kualiti" name="quality_score" type="number" value={form.quality_score} onChange={inputChange('quality_score', 'number')} />
            <FormTextarea label="Kekuatan" name="strengths" value={form.strengths} onChange={e => up('strengths', e.target.value)} />
            <FormTextarea label="Penambahbaikan" name="improvements" value={form.improvements} onChange={e => up('improvements', e.target.value)} />
            <div className="col-span-2 flex gap-4">
              <FormCheckbox label="Perlu Latihan" checked={form.needs_training} onChange={checkChange('needs_training')} />
              <FormCheckbox label="Perlu Konsultasi" checked={form.needs_consultation} onChange={checkChange('needs_consultation')} />
            </div>
          </>
        );

      case 'attendance':
        return (
          <>
            <StaffSelect staff={activeStaff} value={form.staff_id} onChange={e => up('staff_id', e.target.value)} />
            <FormInput label="Tarikh" name="date" type="date" required value={form.date} onChange={inputChange('date', 'text')} />
            <FormSelect label="Status" name="status" options={dash?.attendance_statuses || []} required value={form.status} onChange={e => up('status', e.target.value)} />
            <FormInput label="Masuk" name="check_in" type="time" value={form.check_in} onChange={inputChange('check_in', 'text')} />
            <FormInput label="Keluar" name="check_out" type="time" value={form.check_out} onChange={inputChange('check_out', 'text')} />
            <FormInput label="Nota" name="notes" value={form.notes} onChange={inputChange('notes', 'text')} />
          </>
        );

      case 'leave':
        return (
          <>
            <StaffSelect staff={activeStaff} value={form.staff_id} onChange={e => up('staff_id', e.target.value)} />
            <FormSelect label="Jenis Cuti" name="leave_type" options={dash?.leave_types || []} required value={form.leave_type} onChange={e => up('leave_type', e.target.value)} />
            <FormInput label="Dari" name="start_date" type="date" required value={form.start_date} onChange={inputChange('start_date', 'text')} />
            <FormInput label="Hingga" name="end_date" type="date" required value={form.end_date} onChange={inputChange('end_date', 'text')} />
            <FormInput label="Hari" name="days" type="number" value={form.days} onChange={inputChange('days', 'number')} />
            <FormSelect label="Status" name="status" options={dash?.leave_statuses || []} value={form.status} onChange={e => up('status', e.target.value)} />
            <FormTextarea label="Sebab" name="reason" value={form.reason} onChange={e => up('reason', e.target.value)} />
          </>
        );

      case 'training':
        return (
          <>
            <StaffSelect staff={activeStaff} value={form.staff_id} onChange={e => up('staff_id', e.target.value)} />
            <FormInput label="Nama Latihan" name="training_name" required value={form.training_name} onChange={inputChange('training_name', 'text')} />
            <FormSelect label="Jenis" name="training_type" options={['Internal', 'External', 'Online', 'Workshop']} required value={form.training_type} onChange={e => up('training_type', e.target.value)} />
            <FormInput label="Penganjur" name="provider" value={form.provider} onChange={inputChange('provider', 'text')} />
            <FormInput label="Tarikh Mula" name="start_date" type="date" required value={form.start_date} onChange={inputChange('start_date', 'text')} />
            <FormInput label="Tarikh Tamat" name="end_date" type="date" value={form.end_date} onChange={inputChange('end_date', 'text')} />
            <FormSelect label="Status" name="status" options={dash?.training_statuses || []} value={form.status} onChange={e => up('status', e.target.value)} />
            <FormInput label="Kos (RM)" name="cost" type="number" value={form.cost} onChange={inputChange('cost', 'number')} />
            <div className="col-span-2">
              <FormCheckbox label="Sijil Diperoleh" checked={form.certificate_obtained} onChange={checkChange('certificate_obtained')} />
            </div>
          </>
        );

      case 'disciplinary':
        return (
          <>
            <StaffSelect staff={activeStaff} value={form.staff_id} onChange={e => up('staff_id', e.target.value)} />
            <FormInput label="Tarikh" name="date" type="date" required value={form.date} onChange={inputChange('date', 'text')} />
            <FormSelect label="Jenis Amaran" name="type" options={dash?.disciplinary_types || []} required value={form.type} onChange={e => up('type', e.target.value)} />
            <FormSelect label="Sebab" name="reason" options={dash?.disciplinary_reasons || []} required value={form.reason} onChange={e => up('reason', e.target.value)} />
            <FormTextarea label="Keterangan" name="description" value={form.description} onChange={e => up('description', e.target.value)} />
            <FormTextarea label="Tindakan" name="action_taken" value={form.action_taken} onChange={e => up('action_taken', e.target.value)} />
            <FormInput label="Dikeluarkan Oleh" name="issued_by" value={form.issued_by} onChange={inputChange('issued_by', 'text')} />
            <FormInput label="Tarikh Tamat" name="expiry_date" type="date" value={form.expiry_date} onChange={inputChange('expiry_date', 'text')} />
          </>
        );

      case 'skill':
        return (
          <>
            <StaffSelect staff={activeStaff} value={form.staff_id} onChange={e => up('staff_id', e.target.value)} />
            <FormInput label="Nama Kemahiran" name="skill_name" required value={form.skill_name} onChange={inputChange('skill_name', 'text')} />
            <FormSelect label="Kategori" name="category" options={['Technical', 'Soft Skill', 'Leadership', 'Tools', 'Language']} required value={form.category} onChange={e => up('category', e.target.value)} />
            <FormSelect label="Tahap Semasa" name="current_level" options={dash?.skill_levels || []} required value={form.current_level} onChange={e => up('current_level', e.target.value)} />
            <FormSelect label="Tahap Diperlukan" name="required_level" options={dash?.skill_levels || []} required value={form.required_level} onChange={e => up('required_level', e.target.value)} />
            <div className="col-span-2">
              <FormCheckbox label="Ada Sijil" checked={form.certified} onChange={checkChange('certified')} />
            </div>
          </>
        );

      case 'contract':
        return (
          <>
            <StaffSelect staff={activeStaff} value={form.staff_id} onChange={e => up('staff_id', e.target.value)} />
            <FormSelect label="Jenis Kontrak" name="contract_type" options={dash?.contract_types || []} required value={form.contract_type} onChange={e => up('contract_type', e.target.value)} />
            <FormInput label="Tarikh Mula" name="start_date" type="date" required value={form.start_date} onChange={inputChange('start_date', 'text')} />
            <FormInput label="Tarikh Tamat" name="end_date" type="date" value={form.end_date} onChange={inputChange('end_date', 'text')} />
            <FormInput label="Tamat Probation" name="probation_end" type="date" value={form.probation_end} onChange={inputChange('probation_end', 'text')} />
            <FormSelect label="Status Probation" name="probation_status" options={['Dalam Percubaan', 'Lulus', 'Gagal', 'Dilanjutkan']} value={form.probation_status} onChange={e => up('probation_status', e.target.value)} />
            <FormInput label="Gaji (RM)" name="salary" type="number" value={form.salary} onChange={inputChange('salary', 'number')} />
          </>
        );

      case 'meeting':
        return (
          <>
            <StaffSelect staff={activeStaff} value={form.staff_id} onChange={e => up('staff_id', e.target.value)} />
            <FormInput label="Tarikh" name="date" type="date" required value={form.date} onChange={inputChange('date', 'text')} />
            <FormInput label="Dikendalikan Oleh" name="conducted_by" required value={form.conducted_by} onChange={inputChange('conducted_by', 'text')} />
            <FormInput label="Durasi (minit)" name="duration_mins" type="number" value={form.duration_mins} onChange={inputChange('duration_mins', 'number')} />
            <FormSelect label="Mood Staf" name="staff_mood" options={dash?.meeting_moods || []} value={form.staff_mood} onChange={e => up('staff_mood', e.target.value)} />
            <FormTextarea label="Topik Dibincang" name="topics_discussed" value={form.topics_discussed} onChange={e => up('topics_discussed', e.target.value)} />
            <FormTextarea label="Action Items" name="action_items" value={form.action_items} onChange={e => up('action_items', e.target.value)} />
            <FormInput label="Tarikh Susulan" name="follow_up_date" type="date" value={form.follow_up_date} onChange={inputChange('follow_up_date', 'text')} />
          </>
        );

      case 'reward':
        return (
          <>
            <StaffSelect staff={activeStaff} value={form.staff_id} onChange={e => up('staff_id', e.target.value)} />
            <FormInput label="Tarikh" name="date" type="date" required value={form.date} onChange={inputChange('date', 'text')} />
            <FormSelect label="Jenis Ganjaran" name="reward_type" options={dash?.reward_types || []} required value={form.reward_type} onChange={e => up('reward_type', e.target.value)} />
            <FormInput label="Tajuk" name="title" required value={form.title} onChange={inputChange('title', 'text')} />
            <FormInput label="Nilai (RM)" name="value" type="number" value={form.value} onChange={inputChange('value', 'number')} />
            <FormInput label="Diberi Oleh" name="awarded_by" value={form.awarded_by} onChange={inputChange('awarded_by', 'text')} />
            <FormTextarea label="Keterangan" name="description" value={form.description} onChange={e => up('description', e.target.value)} />
          </>
        );

      case 'exit':
        return (
          <>
            <StaffSelect staff={activeStaff} value={form.staff_id} onChange={e => up('staff_id', e.target.value)} />
            <FormInput label="Tarikh Akhir" name="last_working_date" type="date" required value={form.last_working_date} onChange={inputChange('last_working_date', 'text')} />
            <FormSelect label="Sebab Keluar" name="exit_reason" options={dash?.exit_reasons || []} required value={form.exit_reason} onChange={e => up('exit_reason', e.target.value)} />
            <FormTextarea label="Sebab Terperinci" name="detailed_reason" value={form.detailed_reason} onChange={e => up('detailed_reason', e.target.value)} />
            <FormInput label="Skor Kepuasan (1-5)" name="satisfaction_score" type="number" min="1" max="5" value={form.satisfaction_score} onChange={inputChange('satisfaction_score', 'number')} />
            <FormInput label="Dikendalikan Oleh" name="conducted_by" value={form.conducted_by} onChange={inputChange('conducted_by', 'text')} />
            <FormInput label="Tarikh Interview" name="conducted_date" type="date" value={form.conducted_date} onChange={inputChange('conducted_date', 'text')} />
            <FormTextarea label="Feedback Pengurusan" name="feedback_management" value={form.feedback_management} onChange={e => up('feedback_management', e.target.value)} />
            <FormTextarea label="Cadangan" name="suggestions" value={form.suggestions} onChange={e => up('suggestions', e.target.value)} />
            <div className="col-span-2 flex gap-4">
              <FormCheckbox label="Akan Cadangkan Syarikat" checked={form.would_recommend} onChange={checkChange('would_recommend')} />
              <FormCheckbox label="Akan Kembali" checked={form.would_return} onChange={checkChange('would_return')} />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {renderFormFields()}
      </div>
      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50">
          Batal
        </button>
        <button type="submit" className="flex-1 px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 flex items-center justify-center gap-2">
          <Save className="w-4 h-4" /> Simpan
        </button>
      </div>
    </form>
  );
};

export default DynamicForm;
